import { useEffect, useRef, useState } from 'react';
import type { AppServices } from '@dcc/rules/app/services.ts';
import type { SheetRecords, MutationRecords } from '@dcc/rules/app/sheetMutation.ts';
import { mutationRouting, persistMutation } from '@dcc/rules/app/sheetMutation.ts';
import { SheetMutationGate } from '@dcc/rules/app/sheetMutationGate.ts';
import { requiresRecovery } from '@dcc/rules/persistence/checkpointCommitter.ts';
import { assertCharacterIntegrity } from '@dcc/rules/app/characterIntegrity.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';

const catalogue = {
  lookupItem: (id: string) => contentRegistry.getItem(id),
  lookupAttack: (id: string) => contentRegistry.getAttack(id),
};

/** What a play action decided. A string is a refusal to show the player. */
export type PlayMutation = (MutationRecords & { note?: string | null; after?: () => void }) | string;

/**
 * The play sheet's records (Character, Session, Runtime) and the one way to
 * change them, ported from the desktop Play Screen.
 *
 * claim the gate -> compute -> save through the right boundary -> adopt what
 * was SAVED -> release. `compute` may await dice (Dice+ rolls take seconds);
 * the gate stays claimed throughout, so no other action on this sheet can
 * interleave. Changes made elsewhere (another device, the GM) are caught by
 * the sheet server's version check when this saves.
 *
 * `onSaveError` lets the host turn sheet-server failures into a message (and
 * reload after a stale save); the records are then re-read here.
 */
export function usePlaySheet(
  services: AppServices,
  characterId: string,
  onSaveError: (error: unknown) => Promise<string>,
  coordinationGate?: SheetMutationGate,
  refreshToken = 0,
) {
  const [records, setRecords] = useState<SheetRecords | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadedToken, setLoadedToken] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const loadKey = `${characterId}:${refreshToken}:${reloadToken}`;
  const gateRef = useRef<SheetMutationGate | null>(null);
  if (gateRef.current === null) gateRef.current = new SheetMutationGate();
  const gate = gateRef.current;

  useEffect(() => {
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    (async () => {
      const blocked = coordinationGate?.claim();
      if (blocked) { retry = setTimeout(() => { if (!cancelled) setReloadToken(t => t + 1); }, 100); return; }
      try {
        // Finish any checkpoint an interrupted save left behind, then read all three.
        await services.checkpoints.recover(characterId);
        const character = await services.repository.load(characterId);
        assertCharacterIntegrity(character, catalogue);
        const session = await services.sessions.loadOrStart(characterId);
        const runtime = await services.runtime.loadOrStart(characterId);
        if (cancelled) return;
        setRecords({ character, session, runtime });
        setLoadedToken(loadKey);
        gate.clearRecovery();
        setError(null);
      } catch (err) {
        if (cancelled) return;
        // Fail closed: no records means no actions can write over state we could not read.
        setRecords(null);
        setError(err instanceof Error ? err.message : 'This crawler could not be loaded for play.');
      } finally { coordinationGate?.release(); }
    })();
    return () => { cancelled = true; if (retry) clearTimeout(retry); };
  }, [services, characterId, reloadToken, gate, refreshToken, loadKey, coordinationGate]);

  async function run(context: string, compute: (current: SheetRecords) => Promise<PlayMutation>): Promise<boolean> {
    if (!records || loadedToken !== loadKey) return false;
    const blocked = gate.claim();
    if (blocked !== null) { setError(blocked.message); return false; }
    let coordinationClaimed = false;
    if (coordinationGate) {
      const coordinated = coordinationGate.claim();
      if (coordinated !== null) {
        gate.release();
        setError(coordinated.message);
        return false;
      }
      coordinationClaimed = true;
    }
    setBusy(true);
    let usedCommitter = false;
    try {
      const plan = await compute(records);
      if (typeof plan === 'string') { setError(plan); return false; }
      usedCommitter = mutationRouting(records, plan).usesCommitter;
      const saved = await persistMutation(records, plan, {
        characters: services.repository,
        sessions: services.sessions,
        runtimes: services.runtime,
        checkpoints: services.checkpoints,
      });
      setRecords(saved.records);
      setError(null);
      if (plan.note !== undefined) setNote(plan.note);
      plan.after?.();
      return true;
    } catch (err) {
      if (usedCommitter && requiresRecovery(err)) {
        gate.requireRecovery();
        setError(`${context} The save was interrupted. Reload this crawler before making more changes.`);
      } else {
        setError(`${context} ${await onSaveError(err)}`);
        setReloadToken((t) => t + 1);
      }
      return false;
    } finally {
      if (coordinationClaimed) coordinationGate?.release();
      gate.release();
      setBusy(false);
    }
  }

  return { records, error, note, busy: busy || loadedToken !== loadKey, run, reload: () => setReloadToken((t) => t + 1) };
}

export type PlaySheetController = ReturnType<typeof usePlaySheet>;
